import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Globe,
  Mail,
  Phone,
  MapPin,
  Building,
  FileText,
  Users,
} from "lucide-react";
import { toast } from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import { api } from "../../services/api/axios";
import {
  useApproveBusinessKycById,
  useRejectBusinessKycById,
} from "../../services/queries/queries";

const BusinessDetailForm = () => {
  const navigate = useNavigate();

  const location = useLocation();

  // Check if business data is passed via location state
  const [businessData, setBusinessData] = useState(
    location?.state?.business || null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [id, setId] = useState("");
  const [signedDocUrls, setSignedDocUrls] = useState({});
  const updateBusinessKyc = useApproveBusinessKycById();
  const rejectBusinessKyc = useRejectBusinessKycById();

  // KYB documents are stored with access_mode: authenticated on Cloudinary
  // and can't be viewed via their raw stored URL - resolve each to a
  // short-lived signed delivery URL before rendering/opening it.
  useEffect(() => {
    const documentMap = businessData?.parsedDocuments?.document;
    if (!documentMap) return;
    let cancelled = false;
    Object.entries(documentMap).forEach(async ([docKey, docValue]) => {
      try {
        const { data } = await api.get("/v1/media/signed-url", {
          params: { url: docValue },
        });
        if (!cancelled) {
          setSignedDocUrls((prev) => ({ ...prev, [docKey]: data.data.signedUrl }));
        }
      } catch {
        // fall back to the raw stored URL for this doc
      }
    });
    return () => {
      cancelled = true;
    };
  }, [businessData?.parsedDocuments?.document]);
  // Parse nested JSON data if exists
  useEffect(() => {
    if (businessData) {
      try {
        // Parse the stringified JSON fields
        if (typeof businessData.documents === "string") {
          businessData.parsedDocuments = JSON.parse(businessData.documents);
        }

        if (typeof businessData.owners === "string") {
          businessData.parsedOwners = JSON.parse(businessData.owners);
        }

        setBusinessData({ ...businessData });
      } catch (error) {
        console.error("Error parsing JSON data:", error);
      }
    }
  }, []);

  useEffect(() => {
    setId(businessData.uuid);
  }, [businessData]);
 

  // Handle form field changes
  const handleChange = (e, section, field) => {
    if (!isEditing) return;

    const value = e.target.value;

    if (section) {
      setBusinessData((prev) => ({
        ...prev,
        parsedDocuments: {
          ...prev.parsedDocuments,
          [section]: {
            ...prev.parsedDocuments[section],
            [field]: value,
          },
        },
      }));
    } else {
      setBusinessData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Handle owner changes
  const handleOwnerChange = (index, field, value) => {
    if (!isEditing) return;

    const updatedOwners = [...businessData.parsedOwners];
    updatedOwners[index] = {
      ...updatedOwners[index],
      [field]: value,
    };

    setBusinessData((prev) => ({
      ...prev,
      parsedOwners: updatedOwners,
    }));
  };

  // Add new owner
  const addOwner = () => {
    if (!isEditing) return;

    setBusinessData((prev) => ({
      ...prev,
      parsedOwners: [
        ...prev.parsedOwners,
        { name: "", ownershipPercentage: 0 },
      ],
    }));
  };

  // Remove owner
  const removeOwner = (index) => {
    if (!isEditing) return;

    const updatedOwners = [...businessData.parsedOwners];
    updatedOwners.splice(index, 1);

    setBusinessData((prev) => ({
      ...prev,
      parsedOwners: updatedOwners,
    }));
  };

  // Save changes
  const saveChanges = () => {
    try {
      // Stringify the JSON fields for saving
      const updatedData = {
        ...businessData,
        documents: JSON.stringify({
          document: businessData.parsedDocuments.document || {},
          industry: businessData.parsedDocuments.industry || "",
          website: businessData.parsedDocuments.website || "",
          contactDetails: businessData.parsedDocuments.contactDetails || {
            email: "",
            phone: "",
          },
          address: businessData.parsedDocuments.address || {
            line1: "",
            line2: "",
            city: "",
            state: "",
            country: "",
          },
        }),
        owners: JSON.stringify(
          businessData.parsedOwners || [{ name: "", ownershipPercentage: 0 }]
        ),
      };

      // In a real implementation, you would send this to an API
      toast.success("Business details updated successfully");
      setIsEditing(false);

      // For demonstration, we're just updating the local state
      setBusinessData(updatedData);
    } catch (error) {
      toast.error("Failed to update business details");
      console.error("Error saving changes:", error);
    }
  };

  // Handle KYC approval

  const approveKYC = async () => {
    setIsApproveModalOpen(false);
    try {
      const promise = updateBusinessKyc.mutateAsync(
        id,
      );

      await toast.promise(promise, {
        loading: "Approving KYC...",
        success: "KYC approved successfully",
      });
    } catch (error) {
      console.error("Error approving KYC:", error.response.data.message);
    }
  };

  // Handle KYC rejection

  const declineKYC = async () => {
    setIsDeclineModalOpen(false);
    try {
      const promise = rejectBusinessKyc.mutateAsync(
        id,
      );

      await toast.promise(promise, {
        loading: "Rejecting KYC...",
        success: "KYC rejected successfully",
      });
    } catch (error) {
      console.log(error);
      console.error("Error rejecting KYC:", error.response.data.message);
    }
  };

  if (!businessData) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />

        {/* Main content — scrolls independently */}
        <div className="flex justify-center items-center flex-1">
          <p className="text-gray-600">
            No business data found. Please go back and select a business.
          </p>
        </div>
      </div>
    );
  }

  const { parsedDocuments, parsedOwners } = businessData;

  const getKycStatusStyles = (status) => {
    const statusStyles = {
      Approved: "bg-green-100 text-green-800",
      Pending: "bg-yellow-100 text-yellow-800",
      Rejected: "bg-red-100 text-red-800",
      "Not Submitted": "bg-gray-100 text-gray-800",
      "In Review": "bg-blue-100 text-blue-800",
    };

    return statusStyles[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="flex overflow-hidden flex-col h-screen md:flex-row">
      {/* Sidebar handles its own desktop/mobile rendering */}
      <Sidebar />

      {/* Main Content */}
      <div className="overflow-y-auto flex-1 p-4 py-7 bg-white md:py-7">
        <div className="px-8 mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold py-2">Business Details</h1>
              <button
                className="flex items-center text-[#013941]"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-1 text-[#013941]" />
                Back
              </button>
            </div>
            <div className="space-x-3">
              {businessData.kycStatus !== "Approved" && (
                <button
                  className="px-6 py-2 bg-emerald-800 text-white rounded-full font-sans"
                  onClick={() => setIsApproveModalOpen(true)}
                >
                  Approve KYC
                </button>
              )}

              {businessData.kycStatus !== "Rejected" && (
                <button
                  className="px-6 py-2 border border-[#EB5757] text-[#EB5757] rounded-full font-sans"
                  onClick={() => setIsDeclineModalOpen(true)}
                >
                  Decline KYC
                </button>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="space-y-6 border border-[#CCF6E5] rounded-3xl px-8 py-6">
            {/* Business Information */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Business Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Legal Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={businessData.legalName || ""}
                      onChange={(e) => handleChange(e, null, "legalName")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Trading Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={businessData.tradingName || ""}
                      onChange={(e) => handleChange(e, null, "tradingName")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Registration Number
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={businessData.registrationNumber || ""}
                      onChange={(e) =>
                        handleChange(e, null, "registrationNumber")
                      }
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Tax Number
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={businessData.taxNumber || ""}
                      onChange={(e) => handleChange(e, null, "taxNumber")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Industry
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={parsedDocuments?.industry || ""}
                    onChange={(e) => handleChange(e, null, "industry")}
                    className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                      isEditing ? "bg-white" : "bg-gray-50"
                    }`}
                    readOnly={!isEditing}
                  />
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Website
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={parsedDocuments?.website || ""}
                    onChange={(e) => handleChange(e, null, "website")}
                    className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                      isEditing ? "bg-white" : "bg-gray-50"
                    }`}
                    readOnly={!isEditing}
                  />
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Description
                </label>
                <textarea
                  value={businessData.description || ""}
                  onChange={(e) => handleChange(e, null, "description")}
                  className={`outline-none font-sans w-full p-3 border rounded-xl text-[#000E10] text-sm font-medium min-h-24 ${
                    isEditing ? "bg-white" : "bg-gray-50"
                  }`}
                  readOnly={!isEditing}
                />
              </div>
            </div>

            {/* Documents Section */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Documents
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {parsedDocuments?.document &&
                  Object.keys(parsedDocuments.document).map((docKey) => {
                    const docValue = parsedDocuments.document[docKey];
                    const displayUrl = signedDocUrls[docKey] || docValue;
                    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
                      docValue
                    ); // Check if the document is an image URL
                    const openDocument = (url) => {
                      window.open(url, "_blank");
                    };
                    return (
                      <div key={docKey} className="border rounded-xl p-4">
                        <div className="flex items-center mb-2">
                          <FileText className="w-5 h-5 text-[#013941] mr-2" />
                          <span className="font-medium text-sm capitalize">
                            {docKey.replace(/([A-Z])/g, " $1").trim()}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 h-56">
                          {isImage ? (
                            <img
                              src={displayUrl}
                              alt={docKey}
                              className="w-full h-56 rounded"
                            />
                          ) : (
                            <span>{docValue}</span>
                          )}
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button
                            className="text-xs text-[#013941] font-medium"
                            onClick={() => openDocument(displayUrl)}
                          >
                            View Document
                          </button>
                        </div>
                      </div>
                    );
                  })}
                {(!parsedDocuments?.document ||
                  Object.keys(parsedDocuments.document).length === 0) && (
                  <div className="col-span-2 border rounded-xl p-4 text-center text-gray-500">
                    No documents uploaded yet
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={parsedDocuments?.contactDetails?.email || ""}
                      onChange={(e) =>
                        handleChange(e, "contactDetails", "email")
                      }
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Phone
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={parsedDocuments?.contactDetails?.phone || ""}
                      onChange={(e) =>
                        handleChange(e, "contactDetails", "phone")
                      }
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Address Line 1
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={parsedDocuments?.address?.line1 || ""}
                      onChange={(e) => handleChange(e, "address", "line1")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Address Line 2
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={parsedDocuments?.address?.line2 || ""}
                      onChange={(e) => handleChange(e, "address", "line2")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    City
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={parsedDocuments?.address?.city || ""}
                      onChange={(e) => handleChange(e, "address", "city")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    State
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={parsedDocuments?.address?.state || ""}
                      onChange={(e) => handleChange(e, "address", "state")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Country
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={parsedDocuments?.address?.country || ""}
                      onChange={(e) => handleChange(e, "address", "country")}
                      className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                        isEditing ? "bg-white" : "bg-gray-50"
                      }`}
                      readOnly={!isEditing}
                    />
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Owners */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-[#828282] text-base py-3">
                  Owners
                </h3>
                {isEditing && (
                  <button
                    onClick={addOwner}
                    className="text-sm text-[#013941] font-medium"
                  >
                    + Add Owner
                  </button>
                )}
              </div>

              {parsedOwners &&
                parsedOwners.map((owner, index) => (
                  <div
                    key={index}
                    className="border border-gray-100 rounded-xl p-4 mb-4"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">Owner {index + 1}</h4>
                      {isEditing && (
                        <button
                          onClick={() => removeOwner(index)}
                          className="text-sm text-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={owner.name || ""}
                            onChange={(e) =>
                              handleOwnerChange(index, "name", e.target.value)
                            }
                            className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                              isEditing ? "bg-white" : "bg-gray-50"
                            }`}
                            readOnly={!isEditing}
                          />
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Ownership Percentage
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={owner.ownershipPercentage || 0}
                            onChange={(e) =>
                              handleOwnerChange(
                                index,
                                "ownershipPercentage",
                                parseFloat(e.target.value)
                              )
                            }
                            className={`outline-none font-sans w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium ${
                              isEditing ? "bg-white" : "bg-gray-50"
                            }`}
                            readOnly={!isEditing}
                          />
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#013941] font-bold">
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* KYC Status */}
            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                KYC Status
              </h3>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Current Status
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={businessData.kycStatus || "Not Submitted"}
                    className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282] bg-gray-50"
                    readOnly
                  />
                  <span
                    className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 ${getKycStatusStyles(
                      businessData.kycStatus
                    )} rounded-lg text-sm`}
                  >
                    {businessData.kycStatus || "Not Submitted"}
                  </span>
                </div>
              </div>
            </div>

            {/* Form Action Buttons - Moved to bottom right */}
            <div className="flex justify-end pt-6 space-x-3">
              {isEditing ? (
                <>
                  <button
                    className="px-6 py-2 border border-gray-300 text-gray-600 rounded-full font-sans"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-2 bg-[#013941] text-white rounded-full font-sans"
                    onClick={saveChanges}
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <button
                  className="px-6 py-2 border border-[#013941] text-[#013941] rounded-full font-sans"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Details
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {isApproveModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-medium mb-4">Approve KYC</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to approve this business's KYC? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-full"
                onClick={() => setIsApproveModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-full"
                onClick={approveKYC}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {isDeclineModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-medium mb-4">Decline KYC</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to decline this business's KYC? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-full"
                onClick={() => setIsDeclineModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-full"
                onClick={declineKYC}
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDetailForm;
